import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, Plus, PencilSimple, Trash, Article } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Table, THead, TBody, TR, TH, TD,
  Badge, Skeleton, EmptyState, Pagination, Tooltip, cn,
} from "../../components/ui";

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "published", label: "Published" },
  { value: "scheduled", label: "Scheduled" },
  { value: "draft", label: "Draft" },
];

const STATUS_BADGE = {
  published: { tone: "success", label: "Published" },
  scheduled: { tone: "warning", label: "Scheduled" },
  draft: { tone: "neutral", label: "Draft" },
};

function StatusBadge({ blog }) {
  const s = blog.effective_status || (blog.status === "draft" || !blog.published_at ? "draft" : "published");
  const meta = STATUS_BADGE[s] || STATUS_BADGE.draft;
  return <Badge tone={meta.tone}>{meta.label}</Badge>;
}

function Blogs() {
  const { checkAccess } = useAccessChecker();

  const [blogId, setBlogId] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [done, setDone] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    getBlogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogId, done, currentPage, searchValue, statusFilter]);

  const getBlogs = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(`/blogs/search?page=${currentPage}&per_page=10`, {
        searchValue,
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      const responseData = response.data;
      if (responseData) {
        setBlogs(responseData.data || []);
        setCurrentPage(responseData.current_page);
        setTotalPages(responseData.last_page);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" }) : "—";

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Blogs"
        subtitle="Write and manage your published articles."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search posts…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              containerClassName="w-full sm:w-64"
            />
            {checkAccess("blogs", "create") && (
              <Link to="/blogs/new">
                <Button icon={Plus}>New post</Button>
              </Link>
            )}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            onClick={() => {
              setCurrentPage(1);
              setStatusFilter(f.value);
            }}
            className={cn(
              "inline-flex h-8 items-center rounded-full border px-3.5 text-[13px] font-medium transition",
              statusFilter === f.value
                ? "border-accent bg-accent text-white"
                : "border-line text-muted hover:border-line-strong hover:text-ink"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Table>
        <THead sticky>
          <TR className="hover:bg-transparent">
            <TH>Post</TH>
            <TH>Status</TH>
            <TH>Category</TH>
            <TH>Date</TH>
            <TH className="text-right">Actions</TH>
          </TR>
        </THead>
        <TBody>
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TR key={i} className="hover:bg-transparent">
                <TD>
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-12 w-16 rounded-lg" />
                    <div className="space-y-2">
                      <Skeleton className="h-3.5 w-48" />
                      <Skeleton className="h-3 w-64" />
                    </div>
                  </div>
                </TD>
                <TD><Skeleton className="h-5 w-20 rounded-full" /></TD>
                <TD><Skeleton className="h-5 w-20 rounded-full" /></TD>
                <TD><Skeleton className="h-3.5 w-24" /></TD>
                <TD><Skeleton className="ml-auto h-7 w-16" /></TD>
              </TR>
            ))}

          {!loading &&
            blogs.map((blog) => (
              <TR key={blog.id}>
                <TD>
                  <div className="flex items-center gap-3">
                    <img
                      src={blog.image || "/placeholder.svg"}
                      alt={blog.title}
                      className="h-12 w-16 rounded-lg border border-line bg-surface-2 object-cover"
                    />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-ink">{blog.title}</div>
                      <div className="line-clamp-1 max-w-[420px] text-xs text-muted">{blog.description}</div>
                    </div>
                  </div>
                </TD>
                <TD><StatusBadge blog={blog} /></TD>
                <TD>{blog.category?.name ? <Badge tone="neutral">{blog.category.name}</Badge> : <span className="text-faint">—</span>}</TD>
                <TD className="text-muted">{formatDate(blog.published_at || blog.created_at)}</TD>
                <TD>
                  <div className="flex items-center justify-end gap-0.5">
                    {checkAccess("blogs", "update") && (
                      <Tooltip label="Edit">
                        <Link to={`/blogs/update/${blog.id}`} className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink">
                          <PencilSimple size={16} />
                        </Link>
                      </Tooltip>
                    )}
                    {checkAccess("blogs", "delete") && (
                      <Tooltip label="Delete">
                        <button
                          onClick={() => {
                            setBlogId(blog.id);
                            setDeleteModal(true);
                          }}
                          className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-red-500/10 hover:text-red-600"
                        >
                          <Trash size={16} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                </TD>
              </TR>
            ))}

          {!loading && blogs.length === 0 && (
            <tr>
              <td colSpan={5}>
                <EmptyState
                  icon={Article}
                  title="No posts yet"
                  description="Write your first article to share news and updates."
                  action={
                    checkAccess("blogs", "create") ? (
                      <Link to="/blogs/new">
                        <Button icon={Plus}>New post</Button>
                      </Link>
                    ) : null
                  }
                />
              </td>
            </tr>
          )}
        </TBody>
      </Table>

      <Pagination
        className="mt-4"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => setCurrentPage(p)}
        summary={`Page ${currentPage} of ${totalPages}`}
      />

      <DeleteModal
        setDeleteId={setBlogId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={blogId}
        Model="blogs"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}

export default Blogs;
